import type { ReactElement } from "react";

import { InfoTooltip } from "@skilitics/designer-ui-components";
import type { ArrayBinaryOperator } from "@skilitics/judy";
import { Operator } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";

import { operatorToEnglish } from "../../helpers";

/** All string array operators in display order */
export const stringArrayOperators: ArrayBinaryOperator[] = [
  Operator.Has,
  Operator.NotHas,
  Operator.First,
  Operator.Last,
  Operator.Contains,
  Operator.NotContains,
  Operator.Matches,
  Operator.NotMatches,
  Operator.MatchesFirst,
  Operator.MatchesLast,
];

type OperatorLabel = { label: ReactElement; plainLabel: string };

const formatStrings = {
  hasTooltip: {
    en: "True if the array contains the exact value.",
  },
  notHasTooltip: {
    en: "True if the array does NOT contain the exact value.",
  },
  firstTooltip: {
    en: "True if the value is the first item in the array.",
  },
  lastTooltip: {
    en: "True if the value is the last item in the array.",
  },
  containsTooltip: {
    en: "True if any item in the array contains the value as a substring.",
  },
  notContainsTooltip: {
    en: "True if no item in the array contains the value as a substring.",
  },
  matchesTooltip: {
    en: "True if any item in the array matches the pattern (use * as wildcard).",
  },
  notMatchesTooltip: {
    en: "True if no item in the array matches the pattern.",
  },
  matchesFirstTooltip: {
    en: "True if the first item in the array matches the pattern.",
  },
  matchesLastTooltip: {
    en: "True if the last item in the array matches the pattern.",
  },
};

type TooltipKey = keyof typeof formatStrings;

const operatorToTooltipKey: Partial<Record<ArrayBinaryOperator, TooltipKey>> = {
  [Operator.Has]: "hasTooltip",
  [Operator.NotHas]: "notHasTooltip",
  [Operator.First]: "firstTooltip",
  [Operator.Last]: "lastTooltip",
  [Operator.Contains]: "containsTooltip",
  [Operator.NotContains]: "notContainsTooltip",
  [Operator.Matches]: "matchesTooltip",
  [Operator.NotMatches]: "notMatchesTooltip",
  [Operator.MatchesFirst]: "matchesFirstTooltip",
  [Operator.MatchesLast]: "matchesLastTooltip",
};

export const useStringArrayOperatorLabels = (): Record<ArrayBinaryOperator, OperatorLabel> => {
  const formatMessage = useFormatMessage(formatStrings);

  return Object.fromEntries(
    stringArrayOperators.map((operator) => {
      const plainLabel = operatorToEnglish(operator) ?? operator;
      const tooltipKey = operatorToTooltipKey[operator];
      const tooltip = tooltipKey ? formatMessage(tooltipKey) : undefined;

      return [
        operator,
        {
          label: tooltip ? (
            <>
              {plainLabel} <InfoTooltip tooltip={tooltip} />
            </>
          ) : (
            <>{plainLabel}</>
          ),
          plainLabel,
        },
      ];
    }),
  ) as Record<ArrayBinaryOperator, OperatorLabel>;
};
